<script lang="ts">
	import type { Page as PageType } from '$models/Page/Page';
	import { browser } from '$app/environment';
	import { beforeNavigate } from '$app/navigation';
	import CartButton from '$components/Cart/CartButton.svelte';
	import Hamburger from '$components/UI/Hamburger.svelte';
	import UserIcon from '$components/UI/Icons/UserIcon.svelte';
	import HeaderMenu from '$components/UI/Menus/HeaderMenu.svelte';
	import LanguageSwitch from '$components/UI/Menus/LanguageSwitch.svelte';
	import ToggleMenu from '$components/UI/Menus/ToggleMenu.svelte';
	import { createMobileMenuState, setMobileMenuState } from '$lib/state/menu.svelte';

	let { page }: { page: PageType } = $props();

	// Prepare the menu interactivity
	let scrollY: number = $state(0);
	let scrollTreshold = $state(0);

	beforeNavigate(() => {
		mobileMenuState.setActive(false);
	});

	// Create and set mobile menu state for use by child components.
	const mobileMenuState = createMobileMenuState();
	setMobileMenuState(mobileMenuState);

	$effect(() => {
		if (!browser) return;

		if (mobileMenuState.isActive()) {
			document.body.classList.add('menu-is-open');
		} else {
			document.body.classList.remove('menu-is-open');
		}

		return () => {
			document.body.classList.remove('menu-is-open');
		};
	});

	$effect(() => {
		if (!browser) return;

		if (!page.layout.ribbon.length) {
			scrollTreshold = 0;
			return;
		}

		const updateScrollThreshold = () => {
			const value = getComputedStyle(document.documentElement).getPropertyValue('--ribbon-height');
			scrollTreshold = parseInt(value, 10) || 0;
		};

		updateScrollThreshold();
		window.addEventListener('resize', updateScrollThreshold);

		return () => window.removeEventListener('resize', updateScrollThreshold);
	});
</script>

<header
	class="header"
	class:has-promotion-bar={page.layout.promotionBar.show}
	class:is-fixed={(page.layout.isNavigationFixed ? scrollY > scrollTreshold : false) ||
		mobileMenuState.isActive()}
>
	{#if page.layout.promotionBar.show}
		<div
			class="header-promotion-bar"
			style:background-color={page.layout.promotionBar.backgroundColor}
		>
			<div class="header-promotion-bar-container container">
				{@html page.layout.promotionBar.text}
			</div>
		</div>
	{/if}
	<div class="header-container container">
		<figure class="header-logo">
			<a href="/">
				{@html page.layout.logo}
			</a>
		</figure>

		{#if page.layout.showNavigation}
			<HeaderMenu navigation={page.layout.navigation} />
		{/if}
		<div class="header-actions">
			<CartButton {page} />
			<div class="header-action">
				<a href={page.layout.pages.accountPage + '/'}>
					<UserIcon />
				</a>
			</div>
			<LanguageSwitch />
			<div class="header-action header-action--hamburger">
				<button class:is-active={mobileMenuState.isActive()} onclick={mobileMenuState.toggle}>
					<Hamburger active={mobileMenuState.isActive()} />
				</button>
			</div>
		</div>
	</div>

	<ToggleMenu navigation={page.layout.navigation} active={mobileMenuState.isActive()} />
</header>
<svelte:window bind:scrollY />

<style lang="postcss" global>
	html:has(.header-promotion-bar) {
		--promotion-bar-height: 74px;

		@media (--md) {
			--promotion-bar-height: 60px;
		}
	}

	.ribbon + .header {
		top: var(--ribbon-height, 0px);
	}

	.header {
		position: absolute;
		z-index: 9997;
		top: 0;
		left: 0;
		right: 0;
		color: var(--header-text-color-fixed);
		background-color: var(--header-mobile-bg-color);
		transition:
			color var(--transition) ease,
			background-color var(--transition) ease;

		@media (--xl) {
			background-color: var(--header-bg-color);
		}

		&.is-fixed {
			position: fixed;
			top: 0 !important;
		}

		.cart-is-open & {
			z-index: 997;
		}

		&-container {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 16px;
			margin: 16px;
			background-color: rgba(255, 255, 255, 0.25);
			border-radius: 8px;
			transition:
				padding var(--transition),
				background-color var(--transition);

			body:not(.cart-is-open) .is-fixed & {
				background-color: rgba(255, 255, 255, 0.6);
				backdrop-filter: blur(10px);
				-webkit-backdrop-filter: blur(10px);
			}

			@media (--xl) {
				padding: 16px 16px;
			}

			@media (--xxl) {
				margin-inline: auto;
				padding: 16px 32px;
			}
		}

		&-logo {
			width: 100%;
			max-width: 76px;
			margin-right: var(--spacing-6);
			transition: max-width var(--transition);

			a {
				display: block;
			}

			svg {
				path {
					fill: var(--header-text-color-fixed);
				}
			}

			@media (--xl) {
				max-width: 102px;
				margin-right: var(--spacing-6);
			}
		}

		&-actions {
			display: flex;
			align-items: center;
			margin-left: auto;
			position: relative;

			@media (--xl) {
				margin-left: auto;
			}
		}

		&-action {
			position: relative;
			margin-left: 4px;

			a,
			button {
				position: relative;
				width: 40px;
				height: 40px;
				display: flex;
				align-items: center;
				justify-content: center;
				color: var(--color-brown);
				border: none;
				border-radius: var(--action-border-radius);
				box-sizing: border-box;
				transition: background-color var(--transition);

				&.is-active {
					background-color: rgba(255, 255, 255, 0.5);
				}

				.is-fixed & {
					color: var(--header-text-color-fixed);
					border-color: var(--header-text-color-fixed);
				}

				&:hover {
					background-color: rgba(255, 255, 255, 0.5);
				}
			}

			&-dot {
				display: block;
				position: absolute;
				top: 10px;
				right: 8px;
				z-index: 1;
				width: 8px;
				height: 8px;
				border-radius: 50%;
				background-color: var(--color-promo);
			}

			&-text {
				position: absolute;
				top: calc(100% + 4px);
				left: 50%;
				transform: translateX(-50%);
				font-size: 12px;
				transition: opacity var(--transition);

				.is-fixed & {
					opacity: 0;
				}
			}

			&:first-child {
				margin-left: 0;
			}

			&--hamburger {
				@media (--xl) {
					display: none;
				}
			}
		}

		&-promotion-bar {
			display: flex;
			align-items: center;
			position: relative;
			z-index: 1;
			text-align: center;
			background-color: var(--color-brown);
			height: var(--promotion-bar-height, 74px);
			padding-block: 16px;

			@media (--md) {
				padding-block: 12px;
			}

			&-container {
				color: var(--color-white);
				font-size: 14px;
				line-height: 1.3;
			}

			strong {
				font-family: var(--font-secondary);
				text-transform: uppercase;
			}

			a {
				text-decoration: underline;
				color: inherit;
				transition: opacity var(--transition);

				&:hover {
					opacity: 0.75;
				}
			}
		}
	}
</style>
